import type {
    Placement,
    ProductArray,
} from '@/components/AnalyticsObserver/types';
import type {
    ProductPlacementViewport,
    SearchConfig,
} from '@/types/contentStack';

import type { ExtendedSearchResult } from './types';

// Determines the number of product columns we will display based on screen size
export function getAvailableColumnCount(isMobile: boolean, isTablet: boolean) {
    if (isMobile) {
        return 2;
    } else if (isTablet) {
        return 3;
    } else {
        return 4;
    }
}

export function hasAdsToPlace(
    criteoEnabled: boolean,
    inGridPlacementsReturned: boolean,
    rowToRenderButterflyOn: number | undefined,
    butterflyPlacementsReturned: boolean,
) {
    // Treat butterfly row as valid only when it is a finite, strictly positive number.
    const isValidButterflyRow =
        typeof rowToRenderButterflyOn === 'number' &&
        Number.isFinite(rowToRenderButterflyOn) &&
        rowToRenderButterflyOn > 0;

    return (
        (criteoEnabled && inGridPlacementsReturned) ||
        (isValidButterflyRow && butterflyPlacementsReturned)
    );
}

function getConfigKey(
    isMobile: boolean,
    isTablet: boolean,
): ProductPlacementViewport {
    if (isMobile) {
        return 'web_mobile';
    } else if (isTablet) {
        return 'web_tablet';
    } else {
        return 'web_desktop';
    }
}

export function parseSponsoredProductPlacementPosition(
    sponsoredPlacementConfig:
        | SearchConfig['sponsored_product_placement']
        | undefined,
    isMobile: boolean,
    isTablet: boolean,
): number[] {
    if (!sponsoredPlacementConfig) {
        return [];
    }

    const configKey = getConfigKey(isMobile, isTablet);
    const config = sponsoredPlacementConfig.find(
        config => config.viewport === configKey,
    )?.product_placement_position;

    return config
        ? config
              .split(',')
              .map(pos => pos.trim())
              // Only allow strictly positive integer positions (e.g., "1", "2", "10")
              // This rejects non-positive values like "0", "-1" and malformed tokens like "3a"
              .filter(pos => pos !== '' && /^[1-9]\d*$/.test(pos))
              .map(pos => parseInt(pos, 10))
        : [];
}

export type PlacementConfig = {
    specificIndexes?: number[];
    everyNth?: number;
    iterative?: boolean;
};

export type AdInjectionConfig = {
    sponsoredProducts?: {
        products: ProductArray;
        placement: Placement;
        config: PlacementConfig;
    };
    butterflyAds?: {
        placements: Array<{
            placement: Placement;
            products: ProductArray;
        }>;
        config: PlacementConfig;
    };
};

/**
 * Injects ads into organic search results based on placement configurations.
 * Handles dynamic indexing - as ads are inserted, subsequent placements account for the new array length.
 */
export function injectAdsIntoSearchResults(
    organicResults: ExtendedSearchResult[],
    sponsoredProducts: ProductArray | undefined,
    sponsoredProductPlacementPosition: number[] | undefined,
    butterflyPlacements: Placement[] | undefined,
    butterflyPlacementConfig:
        | {
              iterative: boolean;
              placement_position_nth_?: number[];
              placement_row_nth_?: number[];
          }
        | undefined,
    columnsPerRow: number,
): ExtendedSearchResult[] {
    // If no ads to inject, return organic results as-is
    if (
        (!sponsoredProducts || !sponsoredProducts.length) &&
        (!butterflyPlacements || !butterflyPlacements.length)
    ) {
        return organicResults;
    }

    // If no organic results, return empty (can't place ads without organic content)
    if (!organicResults || organicResults.length === 0) {
        return [];
    }

    // Generate sorted list of all ad placements
    const adQueue: Array<{
        targetPosition: number; // Target position in result array (before adjustments)
        type: 'sponsored' | 'butterfly';
        adIndex: number;
    }> = [];

    // Generate sponsored product placements if we have products to
    if (sponsoredProducts && sponsoredProducts.length) {
        // Do legacy behavior, if we don't have specific index configuration, fall back to injecting at the end of each row
        if (
            sponsoredProductPlacementPosition &&
            sponsoredProductPlacementPosition.length > 0
        ) {
            // Use specific indexes as target positions
            sponsoredProductPlacementPosition.forEach((position, i) => {
                if (i >= sponsoredProducts.length) {
                    return;
                }

                adQueue.push({
                    targetPosition: position - 1, // adjust for 0-based index
                    type: 'sponsored',
                    adIndex: i,
                });
            });
        } else {
            // Place at every the end of each row based on screen size (e.g., every 4 items for desktop)
            let currentPosition = 0; // 0-based
            let adIdx = 0;

            while (adIdx < sponsoredProducts.length) {
                adQueue.push({
                    targetPosition: currentPosition + columnsPerRow - 1, // place at end of row
                    type: 'sponsored',
                    adIndex: adIdx,
                });
                currentPosition += columnsPerRow;
                adIdx++;
            }
        }
    }

    // Generate butterfly ad placements if we have ads and know where to put them
    if (
        butterflyPlacements &&
        butterflyPlacements.length &&
        butterflyPlacementConfig
    ) {
        const { placement_row_nth_, iterative } = butterflyPlacementConfig;

        if (placement_row_nth_ && placement_row_nth_.length > 0) {
            if (iterative) {
                // Iterative mode: repeat the interval from placement_row_nth_[0]
                // Place at the start of every Nth row by decreasing the row count by one, since if we want it on the second
                // row, we want to place it immediately after the first row has ended
                const interval = placement_row_nth_[0];
                if (typeof interval === 'number') {
                    let currentRow = interval - 1;
                    let indexOfAdInResponse = 0;

                    while (indexOfAdInResponse < butterflyPlacements.length) {
                        const targetPosition = currentRow * columnsPerRow;
                        adQueue.push({
                            targetPosition,
                            type: 'butterfly',
                            adIndex: indexOfAdInResponse,
                        });
                        currentRow += interval;
                        indexOfAdInResponse++;
                    }
                }
            } else {
                // Non-iterative mode: use each value from placement_row_nth_ sequentially
                // Place first ad at row placement_row_nth_[0], second ad at row placement_row_nth_[1], etc.
                placement_row_nth_.forEach((rowNth, index) => {
                    if (index < butterflyPlacements.length) {
                        // Convert row number to position (subtract 1 for 0-based indexing)
                        const targetRow = rowNth - 1;
                        const targetPosition = targetRow * columnsPerRow;
                        adQueue.push({
                            targetPosition,
                            type: 'butterfly',
                            adIndex: index,
                        });
                    }
                });
            }
        }
    }

    // Sort ad queue by target position
    adQueue.sort((a, b) => a.targetPosition - b.targetPosition);

    const result: ExtendedSearchResult[] = organicResults.slice();

    for (const ad of adQueue) {
        if (
            ad.type === 'sponsored' &&
            sponsoredProducts &&
            ad.adIndex < sponsoredProducts.length &&
            ad.targetPosition <= result.length // Ensure we don't place beyond results or we just end up with a row of sponsored products at the end
        ) {
            // If the location we want to insert is already an ad, skip it
            if (
                result[ad.targetPosition]?.isCriteoProductTileAd ||
                result[ad.targetPosition]?.isCriteoButterflyAd ||
                result[ad.targetPosition]?.isCriteoButterflyFiller
            ) {
                continue;
            }

            result.splice(ad.targetPosition, 0, {
                isCriteoProductTileAd: true,
                criteoIndex: ad.adIndex,
            });
        } else if (
            ad.type === 'butterfly' &&
            butterflyPlacements &&
            ad.adIndex < butterflyPlacements.length &&
            ad.targetPosition <= result.length
        ) {
            result.splice(
                ad.targetPosition,
                0,
                {
                    isCriteoButterflyAd: true,
                    criteoIndex: ad.adIndex,
                },
                {
                    isCriteoButterflyFiller: true,
                },
            );
        }
    }

    return result;
}
